import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Bot } from 'grts-utils/types/Bot';

import { ApiSubscription, Subscription } from '../types/subscription';
import { selectUserId } from '../store/slices/user';
import {
  selectDemoEndTimestamp,
  selectDemoStartTimestamp,
  selectIsDemo,
} from '../store/slices/demo';
import {
  useGetBotQuery,
  useGetSubscriptionDetailsQuery,
} from '../store/services/api';
import {
  useGetDemoBotQuery,
  useGetDemoSubscriptionDetailsQuery,
} from '../store/services/demoApi';

interface Result {
  bot: Bot | undefined;
  detail: ApiSubscription | undefined;
}

/**
 * Fetches the Bot + full ApiSubscription detail for a subscription, picking
 * the real-user or demo endpoint transparently, and (in demo mode) clipping
 * the detail's `chartData.dataPoints.*` arrays + `transactions` /
 * `compactTransactions` lists to the current Settings date window.
 *
 * The Subscription Content screen consumes only `{ bot, detail }` — it has no
 * direct demo awareness.
 */
const useSubscriptionDetailSource = (
  subscription: Subscription | null,
): Result => {
  const userId = useSelector(selectUserId);
  const isDemo = useSelector(selectIsDemo);
  const demoStart = useSelector(selectDemoStartTimestamp);
  const demoEnd = useSelector(selectDemoEndTimestamp);

  const { data: realBot } = useGetBotQuery(
    subscription?.botFriendlyName ?? '',
    { skip: !subscription?.botFriendlyName || isDemo },
  );
  const { data: demoBotResp } = useGetDemoBotQuery(
    { baseAsset: subscription?.baseAsset ?? '' },
    { skip: !isDemo || !subscription?.baseAsset },
  );
  const bot = isDemo ? demoBotResp?.bot ?? undefined : realBot;

  // Full subscription detail — the response carries the asset chart series
  // the store slice strips out (needed for the Asset Balance line + axis).
  const { data: realDetail } = useGetSubscriptionDetailsQuery(
    { userId, subscriptionId: subscription?.id ?? -1 },
    { skip: !userId || !subscription?.id || isDemo },
  );
  const { data: demoDetailResp } = useGetDemoSubscriptionDetailsQuery(
    { baseAsset: subscription?.baseAsset ?? '' },
    { skip: !isDemo || !subscription?.baseAsset },
  );
  const rawDetail = isDemo
    ? demoDetailResp?.subscriptionDetails ?? undefined
    : realDetail;

  const detail = useMemo<ApiSubscription | undefined>(() => {
    if (!rawDetail) return rawDetail;
    if (!isDemo) return rawDetail;
    if (demoStart == null && demoEnd == null) return rawDetail;
    const inWindow = (ts: number) =>
      (demoStart == null || ts >= demoStart) &&
      (demoEnd == null || ts <= demoEnd);
    const cd: any = rawDetail.chartData;
    const clippedChart = cd
      ? {
          ...cd,
          dataPoints: {
            ...cd.dataPoints,
            total: {
              stable: (cd.dataPoints?.total?.stable ?? []).filter((p: any) =>
                inWindow(p.timestamp),
              ),
              asset: (cd.dataPoints?.total?.asset ?? []).filter((p: any) =>
                inWindow(p.timestamp),
              ),
            },
            noAlgorithmicProgression: (
              cd.dataPoints?.noAlgorithmicProgression ?? []
            ).filter((p: any) => inWindow(p.timestamp)),
            stable: cd.dataPoints?.stable
              ? {
                  all: (cd.dataPoints.stable.all ?? []).filter((p: any) =>
                    inWindow(p.timestamp),
                  ),
                  accumulative: (
                    cd.dataPoints.stable.accumulative ?? []
                  ).filter((p: any) => inWindow(p.timestamp)),
                }
              : undefined,
            asset: cd.dataPoints?.asset
              ? {
                  all: (cd.dataPoints.asset.all ?? []).filter((p: any) =>
                    inWindow(p.timestamp),
                  ),
                  accumulative: (cd.dataPoints.asset.accumulative ?? []).filter(
                    (p: any) => inWindow(p.timestamp),
                  ),
                }
              : undefined,
            compactTransactions: (
              cd.dataPoints?.compactTransactions ?? []
            ).filter((p: any) => inWindow(p.timestamp)),
          },
        }
      : undefined;
    const clippedTransactions = (rawDetail.transactions ?? []).filter(
      (t: any) => inWindow(t.transactionTimestamp || t.timestamp || 0),
    );
    const clippedCompact = (rawDetail.compactTransactions ?? []).filter(
      (t: any) => inWindow(t.transactionTimestamp || t.timestamp || 0),
    );
    return {
      ...rawDetail,
      chartData: clippedChart,
      transactions: clippedTransactions,
      compactTransactions: clippedCompact,
    };
  }, [rawDetail, isDemo, demoStart, demoEnd]);

  return { bot, detail };
};

export default useSubscriptionDetailSource;
